import { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Check, MapPin, Camera, FileText, CalendarCheck, ShieldCheck, DollarSign,
  Snowflake, ArrowRight, ArrowLeft, Zap, Award,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import QuoteForm from "@/components/QuoteForm";
import { getLocation, locations } from "@/data/locations";

const SITE = "https://heatpumpsimple.com";
const HERO_IMG = "https://images.unsplash.com/photo-1781797221329-c3d083ab6d99?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBzdWJ1cmJhbiUyMGhvdXNlJTIwZXh0ZXJpb3J8ZW58MHx8fHwxNzgyMDc1MjMxfDA&ixlib=rb-4.1.0&q=85";

const steps = [
  { icon: MapPin, title: "Enter Your Address" },
  { icon: Camera, title: "Upload Photos" },
  { icon: FileText, title: "Get Pricing & Equipment" },
  { icon: CalendarCheck, title: "Choose Financing & Schedule" },
];

const benefits = [
  { icon: DollarSign, title: "Transparent Pricing", desc: "Know what you'll pay before any in-home appointment." },
  { icon: ShieldCheck, title: "No High-Pressure Sales", desc: "Avoid multiple contractor visits and confusing quotes." },
  { icon: Snowflake, title: "Cold Climate Equipment", desc: "Systems rated for full New England winters." },
  { icon: Zap, title: "Rebates & Financing", desc: "Stack eligible incentives and spread payments over time." },
];

const baseFaqs = [
  { q: "How accurate are online quotes?", a: "Our goal is to provide highly accurate estimates using your address, home details, photos, and information about your existing heating and cooling system. In some cases, additional information or a verification visit may be needed before finalizing your installation price." },
  { q: "Will I need a home visit?", a: "Many heat pump installs and replacements can be quoted remotely using photos and property information. For more complex projects, we may recommend a site visit to confirm equipment sizing, electrical requirements, or installation details before work begins." },
  { q: "Can I finance my installation?", a: "Yes. We plan to offer financing options that allow homeowners to spread payments over time, making it easier to upgrade to an energy-efficient heating and cooling system." },
];

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.08, ease: "easeOut" } }),
};

const scrollToQuote = () => document.getElementById("quote")?.scrollIntoView({ behavior: "smooth" });

function setMeta(name, content, attr = "name") {
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setCanonical(href) {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

const Logo = () => (
  <Link to="/" className="flex items-center gap-2" data-testid="brand-logo">
    <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-blue-600 text-white">
      <Snowflake className="h-5 w-5" strokeWidth={2} />
    </div>
    <span className="font-heading text-xl font-bold tracking-tight text-slate-950">
      Heat<span className="text-blue-600">Pump</span>HQ
    </span>
  </Link>
);

export default function LocationPage({ slug: slugProp }) {
  const params = useParams();
  const slug = slugProp || params.slug;
  const loc = getLocation(slug);

  useEffect(() => {
    if (!loc) return;
    const canonical = `${SITE}/heat-pump-installation-${loc.slug}`;
    document.title = loc.title;
    setMeta("description", loc.description);
    setCanonical(canonical);
    setMeta("og:title", loc.title, "property");
    setMeta("og:description", loc.description, "property");
    setMeta("og:url", canonical, "property");
    return () => {
      document.title = "Heat Pump Installation Made Simple | Get Your Price Online";
    };
  }, [loc]);

  if (!loc) return <Navigate to="/" replace />;

  const faqs = [
    { q: `Will rebates and incentives be included in ${loc.town}?`, a: `Yes. For ${loc.town}, ${loc.stateFull} homeowners, our goal is to help identify available ${loc.rebateProgram} rebates along with federal and other local incentives and incorporate eligible amounts into your estimate.` },
    ...baseFaqs,
  ];

  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-950">
      {/* NAV */}
      <header className="sticky top-0 z-50 border-b border-slate-200 bg-white/80 backdrop-blur-xl">
        <nav className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 md:px-12">
          <Logo />
          <Button onClick={scrollToQuote} className="rounded-full bg-blue-600 px-6 font-medium text-white hover:bg-blue-700" data-testid="loc-nav-cta">
            Get My Quote
          </Button>
        </nav>
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden bg-gradient-to-br from-blue-50 via-sky-50 to-emerald-50">
        <div className="pointer-events-none absolute -right-24 -top-10 h-96 w-96 rounded-full bg-blue-200/40 blur-3xl" />
        <div className="pointer-events-none absolute -left-24 bottom-0 h-80 w-80 rounded-full bg-emerald-200/40 blur-3xl" />
        <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-6 py-16 md:grid-cols-2 md:px-12 md:py-24">
          <motion.div initial="hidden" animate="show" variants={fadeUp}>
            <p className="mb-4 flex items-center gap-1.5 text-sm font-semibold uppercase tracking-[0.15em] text-blue-600">
              <MapPin className="h-4 w-4" /> {loc.town}, {loc.state} · {loc.region}
            </p>
            <h1 className="font-heading text-4xl font-bold leading-tight tracking-tight text-slate-950 sm:text-5xl lg:text-6xl">
              Heat Pump Installation in {loc.town}, {loc.state}
            </h1>
            <h2 className="mt-4 font-heading text-xl font-medium leading-snug text-slate-700 sm:text-2xl">
              Get your heat pump &amp; ductless mini split price online — before anyone visits your home.
            </h2>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-slate-600 lg:text-lg">
              {loc.intro[0]}
            </p>
            <div className="mt-8 flex flex-col gap-4 sm:flex-row">
              <Button onClick={scrollToQuote} className="h-auto rounded-full bg-blue-600 px-8 py-4 text-base font-medium text-white shadow-sm hover:bg-blue-700" data-testid="loc-hero-cta">
                Get My Quote <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Link to="/">
                <Button variant="outline" className="h-auto w-full rounded-full border-slate-300 bg-transparent px-8 py-4 text-base font-medium text-slate-900 hover:bg-slate-100" data-testid="loc-home-link">
                  <ArrowLeft className="mr-2 h-4 w-4" /> Back to Home
                </Button>
              </Link>
            </div>
            <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm font-medium text-slate-700">
              <span className="flex items-center gap-2"><Check className="h-4 w-4 text-emerald-500" strokeWidth={3} /> {loc.utility}</span>
              <span className="flex items-center gap-2"><Award className="h-4 w-4 text-emerald-500" /> {loc.rebateProgram}</span>
              <span className="flex items-center gap-2"><Snowflake className="h-4 w-4 text-emerald-500" /> Cold Climate Rated</span>
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6 }}>
            <img src={HERO_IMG} alt={`Heat pump installation for homes in ${loc.town}, ${loc.stateFull}`} className="aspect-[4/3] w-full rounded-3xl object-cover shadow-2xl" data-testid="loc-hero-image" />
          </motion.div>
        </div>
      </section>

      {/* LOCAL CONTENT */}
      <section className="bg-white py-20 md:py-28">
        <div className="mx-auto max-w-3xl px-6 md:px-12">
          <p className="text-sm font-semibold uppercase tracking-[0.15em] text-blue-600">Built for {loc.county}</p>
          <h2 className="mt-3 font-heading text-3xl font-bold tracking-tight text-slate-950 sm:text-4xl">
            Why {loc.town} homeowners are switching to heat pumps
          </h2>
          {loc.intro.map((p, i) => (
            <p key={i} className="mt-5 text-base leading-relaxed text-slate-600 lg:text-lg">{p}</p>
          ))}
          <ul className="mt-8 space-y-3">
            {loc.localNotes.map((note, i) => (
              <li key={i} className="flex gap-3 text-base text-slate-700">
                <Check className="mt-1 h-5 w-5 shrink-0 text-emerald-500" strokeWidth={2.5} />
                <span>{note}</span>
              </li>
            ))}
          </ul>
          <p className="mt-8 text-sm text-slate-500">
            Also serving nearby: {loc.nearby.join(", ")}.
          </p>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="bg-gradient-to-b from-white via-sky-50/60 to-white py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <h2 className="text-center font-heading text-3xl font-bold tracking-tight text-slate-950 sm:text-4xl">
            A better way to buy in {loc.town}
          </h2>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {benefits.map((b, i) => (
              <motion.div key={b.title} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}
                className="rounded-2xl border border-slate-200 bg-white p-7 shadow-sm transition-transform hover:-translate-y-1 hover:shadow-md" data-testid={`loc-benefit-${i}`}>
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600">
                  <b.icon className="h-5 w-5" strokeWidth={1.5} />
                </div>
                <h3 className="mt-4 font-heading text-lg font-semibold text-slate-950">{b.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{b.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <h2 className="text-center font-heading text-3xl font-bold tracking-tight text-slate-950 sm:text-4xl">How It Works</h2>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {steps.map((s, i) => (
              <div key={s.title} className="rounded-2xl border border-slate-200 bg-white p-6 text-center shadow-sm" data-testid={`loc-step-${i + 1}`}>
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-blue-600 text-white">
                  <s.icon className="h-6 w-6" strokeWidth={1.5} />
                </div>
                <p className="mt-3 text-xs font-bold uppercase tracking-wider text-blue-600">Step {i + 1}</p>
                <h3 className="mt-1 font-heading text-base font-semibold text-slate-950">{s.title}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* QUOTE FORM */}
      <section id="quote" className="relative overflow-hidden bg-gradient-to-br from-emerald-50 via-sky-50 to-blue-50 py-20 md:py-28">
        <div className="pointer-events-none absolute right-0 top-1/4 h-72 w-72 rounded-full bg-blue-200/30 blur-3xl" />
        <div className="relative mx-auto max-w-3xl px-6 md:px-12">
          <div className="mx-auto mb-10 max-w-2xl text-center">
            <h2 className="font-heading text-3xl font-bold tracking-tight text-slate-950 sm:text-4xl">
              Get your {loc.town} heat pump estimate
            </h2>
            <p className="mt-4 text-base leading-relaxed text-slate-600">
              Tell us about your home and current system. We'll prepare transparent pricing,
              {` ${loc.rebateProgram} `}rebate guidance, and financing options for your {loc.town} property.
            </p>
          </div>
          <QuoteForm />
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white py-20 md:py-28">
        <div className="mx-auto max-w-3xl px-6 md:px-12">
          <h2 className="text-center font-heading text-3xl font-bold tracking-tight text-slate-950 sm:text-4xl">
            {loc.town} Heat Pump FAQs
          </h2>
          <Accordion type="single" collapsible className="mt-10" data-testid="loc-faq">
            {faqs.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="border-b border-slate-200">
                <AccordionTrigger className="text-left font-heading text-lg font-medium text-slate-950 hover:no-underline" data-testid={`loc-faq-trigger-${i}`}>
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-base leading-relaxed text-slate-600">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-gradient-to-br from-blue-950 via-blue-900 to-slate-900 py-20 md:py-28">
        <div className="mx-auto max-w-3xl px-6 text-center md:px-12">
          <h2 className="font-heading text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-5xl">
            Ready to upgrade your {loc.town} home?
          </h2>
          <p className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-slate-300 lg:text-lg">
            Join the early access list and get transparent heat pump pricing online.
          </p>
          <Button onClick={scrollToQuote} className="mt-8 h-auto rounded-full bg-emerald-500 px-10 py-4 text-base font-medium text-white shadow-sm hover:bg-emerald-600" data-testid="loc-final-cta">
            Get Early Access <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-slate-200 bg-white py-12">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-center">
            <Logo />
            <p className="text-sm text-slate-500">© {new Date().getFullYear()} HeatPumpHQ. Serving Northeast homeowners.</p>
          </div>
          <div className="mt-8 border-t border-slate-100 pt-6">
            <p className="text-sm font-semibold text-slate-700">Service Areas</p>
            <div className="mt-3 flex flex-wrap gap-x-5 gap-y-2">
              {locations.map((l) => (
                <Link key={l.slug} to={`/heat-pump-installation-${l.slug}`} className="text-sm text-slate-500 transition-colors hover:text-blue-600" data-testid={`footer-area-${l.slug}`}>
                  {l.town}, {l.state}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
